import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchShiftHandoverStatus } from '@/features/alpha/alpha.service';
import type { ShiftHandoverStatus } from '@/features/alpha/types/alpha.types';
import { logger } from '@/shared/lib/logger';

/**
 * The End-of-Shift Handover card's data and its "shown once per shift" rule.
 *
 * WHAT THIS HOOK DOES NOT DO: compose, summarise, count — or generate. It polls
 * Conv-AI's STATUS peek, which is a cache read: it reports the agency's current
 * shift, whether a handover already exists for it, and whether the shift is close
 * enough to its end to offer one. A handover is only ever generated when the
 * dispatcher opens the chat, so no background tab starts one.
 *
 *   card shown  a handover EXISTS for this shift (the incoming dispatcher reads
 *               the outgoing one's), OR the shift is CLOSING and none exists yet
 *               (the outgoing dispatcher is offered one)
 *   dismissed   sessionStorage `alpha-handover-dismissed` = "{shift start}:{shift end}"
 *               — tab-local, so the next shift shows the card again
 *
 * `enabled` gates the poll (the chat permission, the feature flag): when false
 * nothing is requested and nothing is shown.
 */
const DISMISSED_KEY = 'alpha-handover-dismissed';
/** The status peek is one Redis read; five minutes is enough to notice a shift roll over. */
const POLL_MS = 5 * 60 * 1000;

const readDismissed = (): string | null => {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
};

const writeDismissed = (value: string): void => {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, value);
  } catch {
    /* storage unavailable — the dismissal lives in memory for this render tree */
  }
};

export const handoverIdentity = (status: ShiftHandoverStatus): string =>
  `${status.shift.start}:${status.shift.end}`;

export interface ShiftHandoverState {
  status: ShiftHandoverStatus | null;
  /** True while the first read is in flight. */
  loading: boolean;
  /** The card should be on screen for this shift. */
  visible: boolean;
  /** True when no handover exists yet and the shift is ending: the card OFFERS to generate one. */
  offersGeneration: boolean;
  dismiss: () => void;
  reload: () => Promise<void>;
}

export const useShiftHandover = (enabled: boolean, convUiBaseUrl?: string): ShiftHandoverState => {
  const [status, setStatus] = useState<ShiftHandoverStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [dismissedFor, setDismissedFor] = useState<string | null>(readDismissed);
  const started = useRef(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await fetchShiftHandoverStatus(convUiBaseUrl));
    } catch (err) {
      // The card is a convenience: a failed peek shows nothing and says nothing.
      logger.debug('[ShiftHandover] status unavailable', {
        error: err instanceof Error ? err.message : String(err),
      });
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [convUiBaseUrl]);

  useEffect(() => {
    if (!enabled || started.current) return undefined;
    started.current = true;
    void reload();
    const timer = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, reload]);

  const dismiss = useCallback(() => {
    if (!status) return;
    const id = handoverIdentity(status);
    writeDismissed(id);
    setDismissedFor(id);
  }, [status]);

  const offersGeneration = status !== null && !status.exists && status.closing;
  const worthShowing = status !== null && (status.exists || status.closing);
  const visible = enabled && worthShowing && status !== null && dismissedFor !== handoverIdentity(status);
  return { status, loading, visible, offersGeneration, dismiss, reload };
};
